import { Field, FieldArray } from 'formik';
import { Checkbox } from 'formik-material-ui';
import _ from 'lodash';
import React, { useContext } from 'react';
import styled from 'styled-components';
import UsersContext from './UsersContext';

const Container = styled.div`
padding-bottom:10px;
padding-left:20px;
display:flex;
flex-wrap: wrap;
`;

const PermissionItem = styled.em`
    padding-right : 20px
    padding-bottom : 5px;
`;

const FieldLabel = styled.div`
padding-right:20px;
`;

const PermissionFieldContainer = styled.div`
    display:flex;
    align-items:center;
    margin-right:16px;
    margin-bottom:4px;
`;

const PermissionItemsRow = styled.div`
    display:flex;
    flex-wrap:wrap;
    align-items:center;
`;

const PermissionGroupsContainer = styled.div`
    width:100%;
`;

const PermissionGroup = styled.div`
    margin-bottom:8px;
`;

const PermissionGroupTitle = styled.div`
    font-size:12px;
    font-weight:600;
    color:#5f6368;
    text-transform:uppercase;
    letter-spacing:0.5px;
    padding-bottom:4px;
`;

const GROUP_ORDER = ['APP', 'GBU', 'FUNCTION', 'OTHER'];

const normalizeGroupName = (permission) => {
    if (!permission) {
        return 'OTHER';
    }

    const rawGroup = (permission.scope || permission.type || '').toString().trim().toUpperCase();

    if (rawGroup === 'FUNC' || rawGroup === 'FUNCTION') {
        return 'FUNCTION';
    }

    if (rawGroup === 'APP' || rawGroup === 'GBU') {
        return rawGroup;
    }

    return rawGroup || 'OTHER';
};

const buildPermissionGroups = (permissionsWithIndex) => {
    const grouped = _.groupBy(permissionsWithIndex, ({ permission }) => normalizeGroupName(permission));

    return Object.keys(grouped)
        .sort((a, b) => {
            const indexA = GROUP_ORDER.indexOf(a);
            const indexB = GROUP_ORDER.indexOf(b);
            const orderA = indexA === -1 ? GROUP_ORDER.length : indexA;
            const orderB = indexB === -1 ? GROUP_ORDER.length : indexB;

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            return a.localeCompare(b);
        })
        .map((groupName) => ({
            groupName,
            items: [...grouped[groupName]].sort((a, b) => {
                const aName = (a.permission && a.permission.name) || '';
                const bName = (b.permission && b.permission.name) || '';
                return aName.localeCompare(bName);
            }),
        }));
};

const UserPermissions = ({ formProps }) => {
    const usersContext = useContext(UsersContext);
    const { selectedUserDetails = {} } = usersContext.users;
    const { permissions = [] } = selectedUserDetails;

    const permissionsWithIndex = permissions.map((permission, index) => ({ permission, index }));
    const groupedPermissions = buildPermissionGroups(permissionsWithIndex);

    if (!usersContext.editingUserDetails) {
        const filteredPermssions = _.filter(permissions, 'hasRole');
        const filteredPermissionsWithIndex = filteredPermssions.map((permission) => ({
            permission,
            index: permissions.findIndex((candidate) => candidate && permission && candidate.id === permission.id),
        }));
        const groupedFilteredPermissions = buildPermissionGroups(filteredPermissionsWithIndex);

        return (
            < Container>
                <PermissionGroupsContainer>
                    {groupedFilteredPermissions.map(({ groupName, items }) => (
                        <PermissionGroup key={groupName}>
                            <PermissionGroupTitle>{groupName}</PermissionGroupTitle>
                            <PermissionItemsRow>
                                {items.map(({ permission, index }) => (
                                    <PermissionItem key={`${groupName}-${index}`}>
                                        {permission.name}
                                    </PermissionItem>
                                ))}
                            </PermissionItemsRow>
                        </PermissionGroup>
                    ))}
                </PermissionGroupsContainer>
            </Container>
        )
    } else {
        return (
            <Container>
                <FieldArray
                    name="permissions"
                    render={() => {
                        return (
                            <PermissionGroupsContainer>
                                {groupedPermissions.map(({ groupName, items }) => (
                                    <PermissionGroup key={groupName}>
                                        <PermissionGroupTitle>{groupName}</PermissionGroupTitle>
                                        <PermissionItemsRow>
                                            {items.map(({ permission, index }) => (
                                                <PermissionFieldContainer key={`${groupName}-${index}`}>
                                                    <Field
                                                        component={Checkbox}
                                                        name={`permissions.${index}.hasRole`}
                                                        type="checkbox"
                                                    />
                                                    <FieldLabel>
                                                        {permission.name}
                                                    </FieldLabel>
                                                </PermissionFieldContainer>
                                            ))}
                                        </PermissionItemsRow>
                                    </PermissionGroup>
                                ))}
                            </PermissionGroupsContainer>
                        )
                    }}
                />
            </Container>
        )
    }
}
export default UserPermissions;